import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * Toont configureerbaar variant-metafield (via block-instellingen) en werkt bij op `variant:update`.
 *
 * @extends {Component}
 */
export class VariantTeaserText extends Component {
  /** @type {Record<string, string>} */
  #htmlByVariantId = {};

  /** @type {(() => void) | undefined} */
  #teardownVariantInput = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.#reloadPayloadFromDom();

    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.#bindVariantInputFallback();
    this.#applyVariantId(this.#resolveInitialVariantId());
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.#teardownVariantInput?.();
    this.#teardownVariantInput = undefined;
    super.disconnectedCallback();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#reloadPayloadFromDom();
    this.#applyVariantId(this.#resolveInitialVariantId());
  }

  /** Herbouw variant-map uit DOM (nodig na combined-listing morph). */
  #reloadPayloadFromDom() {
    const payload = this.querySelector('script[type="application/json"].variant-teaser-text__payload');
    if (!payload?.textContent) {
      this.#htmlByVariantId = {};
      return;
    }
    try {
      const parsed = JSON.parse(payload.textContent);
      this.#htmlByVariantId =
        parsed && typeof parsed === 'object'
          ? /** @type {Record<string, string>} */ (parsed)
          : {};
    } catch {
      this.#htmlByVariantId = {};
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (!(event instanceof VariantUpdateEvent)) return;
    const id = event.detail.resource?.id;
    if (id == null || id === '') return;
    this.#reloadPayloadFromDom();
    this.#applyVariantId(String(id));
  };

  #productFormScope() {
    return (
      this.closest('product-form-component') ??
      this.closest('.product-information')?.querySelector('product-form-component') ??
      this.closest('.product-details')?.querySelector('product-form-component')
    );
  }

  #resolveInitialVariantId() {
    const urlParams = new URLSearchParams(window.location.search);
    const fromUrl = urlParams.get('variant');
    if (fromUrl) return fromUrl;

    const fromDataset = this.dataset.initialVariantId;
    if (fromDataset) return fromDataset;

    const productForm = this.#productFormScope();
    if (productForm) {
      const variantIdInput =
        productForm.querySelector('[ref="variantId"]') ?? productForm.querySelector('input[name="id"]');
      if (variantIdInput instanceof HTMLInputElement && variantIdInput.value) {
        return variantIdInput.value;
      }
    }

    return null;
  }

  #bindVariantInputFallback() {
    const productForm = this.#productFormScope();
    const variantIdInput =
      productForm?.querySelector('[ref="variantId"]') ??
      productForm?.querySelector('input[name="id"]');

    if (!(variantIdInput instanceof HTMLInputElement)) return;

    const onChange = () => {
      if (variantIdInput.value) this.#applyVariantId(variantIdInput.value);
    };

    variantIdInput.addEventListener('change', onChange);

    const observer = new MutationObserver(() => {
      if (variantIdInput.value) onChange();
    });
    observer.observe(variantIdInput, { attributes: true, attributeFilter: ['value'] });

    this.#teardownVariantInput = () => {
      variantIdInput.removeEventListener('change', onChange);
      observer.disconnect();
    };
  }

  /**
   * @param {string | null} variantId
   */
  #applyVariantId(variantId) {
    const panel = this.querySelector('.variant-teaser-text__content');
    if (!(panel instanceof HTMLElement)) return;

    if (variantId == null || variantId === '') {
      panel.innerHTML = '';
    } else {
      const html = this.#htmlByVariantId[String(variantId)] ?? '';
      panel.innerHTML = this.#normalizeHtml(html);
    }

    this.#syncVisibility(panel);
  }

  /**
   * @param {string} html
   * @returns {string}
   */
  #normalizeHtml(html) {
    if (typeof html !== 'string') return '';

    const trimmed = html.trim();
    if (!trimmed) return '';

    const template = document.createElement('template');
    template.innerHTML = trimmed;
    const text = template.content.textContent?.trim() ?? '';
    return text ? trimmed : '';
  }

  /**
   * Verberg lege teaser (ook visueel lege rich text zoals `<p></p>`).
   *
   * @param {HTMLElement} panel
   */
  #syncVisibility(panel) {
    const hasContent = panel.textContent.trim().length > 0;
    const hasHeading = this.querySelector('.variant-teaser-text__heading') != null;

    this.classList.toggle('variant-teaser-text--has-content', hasContent);
    panel.hidden = !hasContent;
    this.hidden = !hasContent && !hasHeading;
  }
}

if (!customElements.get('variant-teaser-text')) customElements.define('variant-teaser-text', VariantTeaserText);
